import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';

import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Tooltip } from '@mantine/core';
import { IconMaximize, IconMinimize } from '@tabler/icons-react';
import React, { Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUploadImage } from '@mygpt/app/attachments';
import { normalizeMarkdownBlankLines, preserveMarkdownBlankLines } from '@mygpt/app/utils/markdownBlankLines';
import { requestFullscreen } from '@/utils/element';

import { Toolbar } from './Toolbar';
import classes from './index.module.css';

export interface TiptapRichEditorProps {
  autoExpand?: boolean;
  placeholder?: string;
  defaultValue?: string;
  readonly?: boolean;
  className?: string;
  onChange?: (value: string) => void;
  onUploadState?: (state: boolean) => void;
  datasetId?: string;
  contentMinHeight?: number;
}

export interface TiptapRichEditorRef {
  setValue: (value: string) => void;
  focus: () => void;
  blur: () => void;
}

export const TiptapRichEditor = React.forwardRef(
  (
    {
      autoExpand,
      className,
      contentMinHeight,
      datasetId,
      defaultValue,
      onChange,
      onUploadState,
      placeholder,
      readonly,
    }: TiptapRichEditorProps,
    ref: Ref<TiptapRichEditorRef>
  ) => {
    const { t } = useTranslation();
    const [focused, setFocused] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const { uploadImage } = useUploadImage();

    // 自定义图片上传扩展
    const CustomImage = Image.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          src: {
            default: null,
            parseHTML: (element) => element.getAttribute('src'),
            renderHTML: (attributes) => {
              if (!attributes.src) {
                return {};
              }
              return { src: attributes.src };
            },
          },
        };
      },
    });

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          code: {},
          codeBlock: {},
          hardBreak: false,
        }),
        Markdown,
        CustomImage,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-blue-600 underline',
          },
        }),

        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        TextStyle,
        Color,
        Highlight.configure({
          multicolor: true,
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableCell,
        TableHeader,
        Placeholder.configure({
          placeholder: placeholder || t('doc.placeholder'),
        }),
      ],
      content: '',
      editable: !readonly,
      onUpdate: ({ editor }) => {
        if (onChange) {
          // 使用官方 Markdown 扩展序列化，保留所有格式
          // 保存前把 &nbsp; 占位段落还原为纯空行，避免占位符写进存储数据
          const markdown = normalizeMarkdownBlankLines(editor.getMarkdown());
          onChange(markdown);
        }
      },
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
    });

    // 初始化内容 - 监听 defaultValue 的变化
    useEffect(() => {
      if (editor && defaultValue !== undefined) {
        // 与 onChange 输出保持同一形态（还原占位段落后）再比较，避免误判为内容变化
        const currentMarkdown = normalizeMarkdownBlankLines(editor.getMarkdown());
        // 只有当内容真的不同时才更新（避免不必要的更新）
        if (currentMarkdown !== defaultValue) {
          // 使用 contentType: 'markdown' 确保正确解析 Markdown 格式
          // 回显前把连续空行显式化为 &nbsp; 占位段落，防止被 markdown 解析折叠掉
          editor.commands.setContent(preserveMarkdownBlankLines(defaultValue || ''), {
            emitUpdate: false,
            contentType: 'markdown',
          });
        }
      }
    }, [editor, defaultValue]);

    // 图片上传处理
    const handleImageUpload = async (file: File) => {
      if (!datasetId) return;

      const setUploadState = onUploadState || (() => {});
      setUploadState(true);

      try {
        const res = await uploadImage({
          file,
          fileType: 'faq_image',
          aiId: datasetId,
        });
        return res.data.url;
      } finally {
        setUploadState(false);
      }
    };

    // 暴露命令式 API
    useImperativeHandle(
      ref,
      () => ({
        setValue: (value: string) => {
          if (editor) {
            editor.commands.setContent(preserveMarkdownBlankLines(value || ''), {
              emitUpdate: false,
              contentType: 'markdown',
            });
          }
        },
        focus: () => {
          editor?.commands.focus();
        },
        blur: () => {
          editor?.blur();
        },
      }),
      [editor]
    );

    // 全屏功能
    useEffect(() => {
      const handleFullscreenChange = () => {
        setFullScreen(document.fullscreenElement !== null);
      };
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
      };
    }, []);

    const handleRequestFullscreen = () => {
      if (containerRef.current) {
        if (!fullScreen) requestFullscreen(containerRef.current);
        else {
          document.exitFullscreen();
        }
      }
    };

    if (!editor) {
      return null;
    }

    return (
      <div
        ref={containerRef}
        style={
          contentMinHeight && !fullScreen
            ? ({ '--tiptap-content-min-height': `${contentMinHeight - 24}px` } as React.CSSProperties)
            : undefined
        }
        className={`relative w-full border border-gray-200 rounded-md text-sm z-10 ${classes.editorRoot} ${
          focused ? classes.editorRootFocus : classes.editorRootNormal
        } ${className || ''} ${readonly ? 'cursor-default border-none bg-transparent' : 'bg-white'}`}
      >
        <div
          className={readonly ? '' : fullScreen ? classes.containerRootFullScreen : classes.containerRoot}
          style={
            !fullScreen
              ? {
                  minHeight: contentMinHeight,
                  maxHeight: autoExpand ? 'none' : undefined,
                  overflowY: autoExpand ? 'visible' : undefined,
                }
              : undefined
          }
        >
          <EditorContent editor={editor} className={classes.content} />
        </div>
        {!readonly && (
          <div
            className={`w-full flex justify-between items-center px-1 border-t border-dashed bg-white border-gray-200 rounded-b-md ${
              classes.toolbarRootBase
            } ${fullScreen ? classes.toolbarFullscreen : classes.toolbarRoot}`}
            onMouseDown={(e) => {
              // 防止工具栏点击导致编辑器失焦
              e.preventDefault();
            }}
          >
            <Toolbar editor={editor} onImageUpload={handleImageUpload} />
            <Tooltip label={fullScreen ? t('exit_full_screen') : t('full_screen')}>
              <span className="hover:text-violet-800 cursor-pointer text-gray-800" onClick={handleRequestFullscreen}>
                {fullScreen ? <IconMinimize width={20} /> : <IconMaximize width={20} />}
              </span>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
);

TiptapRichEditor.displayName = 'TiptapRichEditor';
